import { useState } from "react";
import {
  Box,
  Button,
  Container,
  Dialog,
  Grid,
  Typography,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { Theme, useTheme } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import Loader from "components/Loader";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { FormattedMessage as T } from "react-intl";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { without } from "lodash";
import {
  AdminListRequestOfBaseFilter,
  MembershipGatedAccessEnabledRequest,
  SkuContentRule,
  Skus,
  IListResponseOfSkuContentRule,
  ListResponseOfSkuContentRule,
  UpdateRuleSequenceRequest,
  Response,
} from "services/KnowfullyAdminClient";
import SkuRuleCreateForm from "./SkuRuleCreateForm";
import OrderableSkuRules from "./OrderableSkuRules";
import { SkuFormProps } from "types/sku-related-types";
import useTrashablePromises from "hooks/useTrashablePromises";

type SkuRulesProps = {
  sku: Skus;
  readonly?: boolean;
};

const useStyles = makeStyles((theme: Theme) => ({
  ruleListHeader: {
    display: "flex",
    alignItems: "center",
    margin: theme.spacing(1, 2),
  },
  editActions: {
    // 100 for the action, 24 for the expand icon
    width: 124,
  },
  ruleBehavior: {
    width: 130,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  accessType: {
    width: 130,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  name: {
    flexGrow: 1,
  },
  dragHandle: {
    width: 30,
  },
  sequenceNumber: {
    width: 30,
  },
}));

const SkuRuleSection = ({
  sku,
  readonly = false,
  ...rest
}: SkuRulesProps & SkuFormProps) => {
  const styles = useStyles(useTheme());
  const { skuService } = useGlobalContext();
  const [createOpen, createOpenActions] = useBoolean(false);
  const [expandedRuleIds, setExpandedRuleIds] = useState<string[]>([]);
  const queryClient = useQueryClient();
  const { registerPromise } = useTrashablePromises();

  const { data: membershipGatedAccessSetting } = useQuery(
    ["membershipGatedAccessSetting", sku.ownerOrganizationId],
    () =>
      skuService.getMembershipGatedAccessEnabled(
        new MembershipGatedAccessEnabledRequest({ organizationId: sku.ownerOrganizationId! })
      ),
    { enabled: !!sku.ownerOrganizationId }
  );

  const isMembershipGatedAccessEnabled =
    membershipGatedAccessSetting?.data?.value === "true";

  const skuRulesResult = useQuery<ListResponseOfSkuContentRule>(
    ["skuRules", sku.id],
    () =>
      skuService.getSkuContentRules(
        sku.id!,
        AdminListRequestOfBaseFilter.fromJS({
          sortField: "sequence",
        })
      ),
    {
      enabled: !!sku.id,
    }
  );

  const skuRules = skuRulesResult.data?.data!;

  const deleteSkuRuleMutation = useMutation<
    Response,
    unknown,
    { skuRuleId: string }
  >(({ skuRuleId }) => skuService.deleteSkuContentRule(sku.id!, skuRuleId), {
    onSettled: () => {
      queryClient.invalidateQueries(["skuRules", sku.id]);
      queryClient.invalidateQueries(["skuRuleDetails", sku.id]);
    },
  });

  const cloneSkuRule = async (skuRuleId: string) => {
    try {
      await skuService.cloneSkuContentRule(sku.id!, skuRuleId);
      queryClient.invalidateQueries(["skuRules", sku.id]);
      queryClient.invalidateQueries(["skuRuleDetails", sku.id]);
    } catch (err: any) {
      console.log(err);
    }
  };

  const updateSkuContentRulesSequence = async (
    orderedSkuRules: SkuContentRule[]
  ) => {
    const orderedContentRuleIds = orderedSkuRules.map((skuRule) => skuRule.id);
    await registerPromise(
      skuService.updateSkuContentRulesSequence(
        sku.id!,
        new UpdateRuleSequenceRequest({ contentRuleIds: orderedContentRuleIds })
      )
    );
  };

  const reorderSkuRulesMutation = useMutation<
    void,
    unknown,
    SkuContentRule[],
    IListResponseOfSkuContentRule | undefined
  >(
    async (orderedSkuRules: SkuContentRule[]) => {
      await updateSkuContentRulesSequence(orderedSkuRules);
    },
    {
      onSettled: () => {
        queryClient.invalidateQueries(["skuRules", sku.id]);
      },
    }
  );

  const toggleExpansion = (skuRuleId: string) => {
    if (expandedRuleIds.includes(skuRuleId)) {
      setExpandedRuleIds(without(expandedRuleIds, skuRuleId));
    } else {
      setExpandedRuleIds([...expandedRuleIds, skuRuleId]);
    }
  };

  if (skuRulesResult.isLoading) return <Loader />;
  if (skuRulesResult.isError) {
    console.log(skuRulesResult.error);
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="skuRulesLoadError" />
        </Alert>
      </Box>
    );
  }

  const onSkuRuleCreated = (id: string) => {
    queryClient.invalidateQueries(["skuRules", sku.id]);
    createOpenActions.setFalse();

    // Expand the new rule by default, so that we can edit it
    setExpandedRuleIds([...expandedRuleIds, id]);
  };

  return (
    <Container>
      {!readonly && (
        <Box mb={3}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Box textAlign="right">
                <Button
                  variant="outlined"
                  type="submit"
                  color="primary"
                  onClick={createOpenActions.setTrue}
                  data-cy="addRuleButton"
                >
                  <T id="addRuleButton" />
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      <Box sx={styles.ruleListHeader}>
        <Box sx={styles.dragHandle} />
        <Box sx={styles.sequenceNumber} />
        <Typography variant="subtitle1" sx={styles.name}>
          <T id="ruleNameLabel" />
        </Typography>
        <Typography variant="subtitle1" sx={styles.ruleBehavior}>
          <T id="skuRuleBehaviorLabel" />
        </Typography>
        <Typography variant="subtitle1" sx={styles.accessType}>
          <T id="skuRuleAccessTypeLabel" />
        </Typography>
        <Box sx={styles.editActions} />
      </Box>

      {skuRules && (
        <OrderableSkuRules
          skuRules={skuRules}
          sku={sku}
          cloneSkuRule={cloneSkuRule}
          deleteSkuRuleMutation={deleteSkuRuleMutation}
          reorderSkuRulesMutation={reorderSkuRulesMutation}
          toggleExpansion={toggleExpansion}
          expandedSkuRuleIds={expandedRuleIds}
          readonly={readonly}
          isMembershipGatedAccessEnabled={isMembershipGatedAccessEnabled}
          {...rest}
        />
      )}

      <Dialog open={createOpen} maxWidth="sm" fullWidth disableEnforceFocus>
        <SkuRuleCreateForm
          onSkuRuleCreated={onSkuRuleCreated}
          onCancel={createOpenActions.setFalse}
          sku={sku}
          isMembershipGatedAccessEnabled={isMembershipGatedAccessEnabled}
        />
      </Dialog>
    </Container>
  );
};

export default SkuRuleSection;
